import {
    Box,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReviewTransforms } from './useReviewTransforms';

// One size in every state: the group's own hover and grouping rules change borders and margins, so all are pinned.
const SEGMENT_SX = {
    '& .MuiToggleButton-root': {
        flex: '1 1 0',
        minWidth: 0,
        height: 26,
        px: 1,
        py: 0,
        fontSize: 11,
        fontWeight: 500,
        lineHeight: 1,
        textTransform: 'none',
        margin: '0 !important',
        border: '1px solid transparent !important',
    },
} as const;

interface ReviewTransformSectionProps {
    readonly transforms: ReviewTransforms;
}

export default function ReviewTransformSection({
    transforms,
}: ReviewTransformSectionProps) {
    const intl = useIntl();
    const { options, selected, select } = transforms;

    if (options.length === 0) {
        return (
            <Typography
                fontSize={11.5}
                color="text.secondary"
                data-testid="replanning-no-transforms"
            >
                <FormattedMessage id="replanning.review.transform.none" />
            </Typography>
        );
    }

    return (
        <Box display="flex" flexDirection="column" gap={0.75}>
            <ToggleButtonGroup
                exclusive
                size="small"
                fullWidth
                value={selected?.sopInstanceUid ?? null}
                onChange={(_, value) => value && select(value)}
                aria-label={intl.formatMessage({
                    id: 'replanning.review.transform',
                })}
                sx={SEGMENT_SX}
                data-testid="replanning-transform-toggle"
            >
                {options.map((option) => (
                    <ToggleButton
                        key={option.sopInstanceUid}
                        value={option.sopInstanceUid}
                        data-testid={`replanning-transform-${option.kind}`}
                    >
                        <FormattedMessage
                            id={`replanning.review.transform.${option.kind}`}
                        />
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
            {selected && (
                <Typography
                    fontSize={11}
                    color="text.secondary"
                    data-testid="replanning-transform-help"
                >
                    <FormattedMessage
                        id={`replanning.review.transform.help.${selected.kind}`}
                    />
                </Typography>
            )}
        </Box>
    );
}
